<template>
  <div :class="$style.wrapper">
    <div :class="$style.chart">
      <Pie
        :chart-id="chartId"
        :data="chartData"
        :options="chartOptions"
        :plugins="resolvedPlugins"
      />
    </div>

    <div
      v-if="showLegend"
      :class="$style.legend"
    >
      <div
        v-for="(label, index) in chartData.labels"
        :key="label"
        :class="$style.legendItem"
      >
        <span
          :class="$style.legendColor"
          :style="{ backgroundColor: chartData.datasets[0].backgroundColor[index] }"
        />
        <span
          :class="$style.legendLabel"
          :title="label"
        >
          {{ label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import { Pie } from 'vue-chartjs';
  import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, CategoryScale, Colors } from 'chart.js';
  import ChartDataLabels from 'chartjs-plugin-datalabels';

  ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, Colors);

  export default {
    name: 'PieChart',

    components: { Pie },

    props: {
      chartId: {
        type: String,
        default: 'pie-chart',
      },

      data: {
        type: Object,
        required: true,
      },

      height: {
        type: Number,
        default: 300,
      },

      showLegend: {
        type: Boolean,
        default: false,
      },

      showTitle: {
        type: Boolean,
        default: true,
      },

      showLabels: {
        type: Boolean,
        default: true,
      },

      colors: {
        type: Array,
        default: null,
      },

      backgroundColor: {
        type: String,
        default: 'white',
      },

      useDataLabels: {
        type: Boolean,
        default: true,
      },

      formatValue: {
        type: Function,
        default: (v) => v,
      },
    },

    data() {
      return {
        bgPlugin: {
          id: 'customCanvasBackgroundColor',
          beforeDraw: (chart, _args, options) => {
            const { ctx } = chart;
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = options.color || 'white';
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          },
        },
      };
    },

    computed: {
      chartData() {
        const defaultColors = ['#36A2EB', '#FF6384', '#FF9F40', '#FFCD56', '#4BC0C0', '#9966FF', '#C9CBCF'];
        return {
          labels: this.data.labels,
          datasets: [
            {
              data: this.data.data,
              backgroundColor:
                this.colors || this.data.labels.map((_, index) => defaultColors[index % defaultColors.length]),
            },
          ],
        };
      },

      chartOptions() {
        return {
          responsive: true,
          maintainAspectRatio: false,

          plugins: {
            colors: {
              forceOverride: true,
            },

            legend: {
              display: !this.showLegend,
              position: 'right',
              labels: {
                boxWidth: 15,
                boxHeight: 15,
                padding: 10,
                font: {
                  size: 12,
                },
              },
            },

            title: {
              display: this.showTitle,
              text: this.data.title || '',
              padding: {
                top: 10,
                bottom: 30,
              },
            },
            datalabels: {
              display: true,
              formatter: (value) => (this.formatValue ? this.formatValue(value) : value),
              color: '#fff',
              backgroundColor: '#404040',
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || context.dataset.label || '';
                  const value = context.raw ?? 0;

                  const formattedValue = this.formatValue ? this.formatValue(value) : value;

                  return `${label}: ${formattedValue}`;
                },
              },
            },
          },
        };
      },

      resolvedPlugins() {
        const plugins = [];

        if (this.useDataLabels) {
          plugins.push(ChartDataLabels);
        }

        plugins.push(this.bgPlugin);

        return plugins;
      },
    },
  };
</script>

<style lang="scss" module>
  .wrapper {
    display: flex;
    gap: 16px;
    height: 100%;
  }

  .chart {
    flex: 1;
    min-width: 0;
  }

  .legend {
    width: 250px;
    overflow-y: auto;
  }

  .legendItem {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
  }

  .legendColor {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    border-radius: 2px;
  }

  .legendLabel {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
